import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import HelpMeDecideContent from 'components/common/helpMeDecide/HelpMeDecideContent';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { useQueryParams } from 'global/hooks/useQuery';
import { INITIAL_WAHOO_HEALTH_BC } from 'global/utils/HelpMeDecideHelper';
import MedicalCriteria1 from 'components/help-me-decide/wahoo-health/GroupMedical/Criteria1';
import MedicalCriteria2 from 'components/help-me-decide/wahoo-health/GroupMedical/Criteria2';
import MedicalCriteria3 from 'components/help-me-decide/wahoo-health/GroupMedical/Criteria3';
import MedicalCriteria4 from 'components/help-me-decide/wahoo-health/GroupMedical/Criteria4';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';

export interface HelpMeDecideWahooHealthProps {
  numberOfMedicalCriteria: number;
}

const HelpMeDecideWahooHealth = observer(({ numberOfMedicalCriteria }: HelpMeDecideWahooHealthProps): JSX.Element => {
  const history = useHistory();
  //  Query params
  const query = useQueryParams();
  const cartId = query.get('cartId') || null;
  const pLineId = query.get('productLineId') || null;
  const groupId = query.get('groupId') || null;

  // Mobx store
  const {
    helpMeDecideStore: { criteriaIndex, setCriteriaIndex, setCurrentProductLine },
    browsingStore: {
      loading,
      currentProductLineId,
      cart,
      allPortfolios,
      productLineAttrs,
      setCurrentProductLineId,
      collectInitialData,
      updateCart,
      getPortfolios,
      getCatalog,
      setLoading,
      setFilteredPortfolios,
    },
    browsingCriteriaModalStore: { updateBrowsingCriteriaWithoutForm },
    browsingFilterStore: { filters, browsingCriteria, filterPortfoliosBy, setBrowsingCriteria },
    domainStore: { domainHeader },
  } = useRootStore();

  useEffect(() => {
    if (cartId && pLineId && groupId) {
      void collectInitialData(groupId, cartId, pLineId as ProductLineId);
      setCurrentProductLine(pLineId as ProductLineId);
      setBrowsingCriteria(INITIAL_WAHOO_HEALTH_BC);
    }
  }, []);

  const convertedCart = toJS(cart);
  const convertedDomain = toJS(domainHeader);
  const convertedFilters = toJS(filters);
  const convertedBc = toJS(browsingCriteria);
  const convertedAllPortfolios = allPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedProductLineAttrs = productLineAttrs.map((attr) => toJS(attr)).slice();

  const effectiveDate = convertedCart.lastUpdateBrowsingCriteria?.effectiveDate;
  const rateSegment = currentProductLineId ? PRODUCT_LINES_CONTENT_MAP[currentProductLineId].rateSegment : 'GROUP';

  const productLineIdFromUrl = (): ProductLineId => {
    return pLineId ? (pLineId as ProductLineId) : 'GroupMedical';
  };

  const getNumberOfCriteria = (): number => {
    switch (pLineId) {
      case 'GroupMedical':
        return numberOfMedicalCriteria;
      default:
        return 0;
    }
  };

  const redirectOnBrowsing = async (): Promise<void> => {
    const selectedProductLineId = productLineIdFromUrl();

    setLoading(true);
    setCurrentProductLineId(selectedProductLineId);
    if (!currentProductLineId) throw new Error('CurrentProductLineId is missing in the store');
    const updatedCart = await updateBrowsingCriteriaWithoutForm(
      convertedCart,
      {
        ...INITIAL_WAHOO_HEALTH_BC,
        ...convertedBc,
      },
      currentProductLineId,
    );
    await runInAction(async () => {
      updateCart(updatedCart);

      const requests = [
        getCatalog(effectiveDate, rateSegment, currentProductLineId, convertedCart.lastUpdateBrowsingCriteria?.state),
        getPortfolios({
          bc: updatedCart.lastUpdateBrowsingCriteria,
          rateSegment,
          sponsorId: convertedDomain.id,
          agencyId: '',
          productLineId: currentProductLineId,
          groupId: updatedCart.groupId,
        }),
      ];
      await Promise.all(requests).then(() => {
        if (convertedFilters) {
          const updatedPortfolios = filterPortfoliosBy(
            [...convertedAllPortfolios],
            convertedFilters,
            convertedProductLineAttrs,
          );
          setFilteredPortfolios(updatedPortfolios);
        }
        setLoading(false);
        history.push(
          `/browsing?groupId=${groupId}&cartId=${cartId}&productLineId=${currentProductLineId}&cartType=browsing&backType=help-me-decide`,
        );
      });
    });
  };

  const changeCriteria = async (type: 'BACK' | 'NEXT'): Promise<void> => {
    const isFirstCriteria = criteriaIndex === 1;
    const isLastCriteria = criteriaIndex === getNumberOfCriteria();

    switch (type) {
      case 'BACK':
        if (!isFirstCriteria) {
          setCriteriaIndex(criteriaIndex - 1);
        } else {
          setBrowsingCriteria({});
          const url = `/popular-plans?groupId=${groupId}&productLineId=${pLineId}&cartId=${cartId}&cartType=popular`;
          history.push(url);
        }
        break;
      case 'NEXT':
        if (isLastCriteria) {
          await redirectOnBrowsing();
        } else {
          setCriteriaIndex(criteriaIndex + 1);
        }
        break;
      default:
        history.goBack();
        break;
    }
  };

  const getCurrentCriteria = (productLineId: ProductLineId | null): JSX.Element => {
    // These props are used to render criteria form
    const commonCriteriaProps = {
      numberOfCriteria: getNumberOfCriteria(),
      minHeight: '53rem',
      loading,
      onContinue: (): void => {
        void changeCriteria('NEXT');
      },
      onBack: (): void => {
        void changeCriteria('BACK');
      },
    };

    switch (productLineId) {
      case 'GroupMedical':
        switch (criteriaIndex) {
          case 1:
            return <MedicalCriteria1 {...commonCriteriaProps} />;
          case 2:
            return <MedicalCriteria2 {...commonCriteriaProps} />;
          case 3:
            return <MedicalCriteria3 {...commonCriteriaProps} />;
          case 4:
            return <MedicalCriteria4 {...commonCriteriaProps} />;
          default:
            return <></>;
        }
      default:
        return <></>;
    }
  };

  return <HelpMeDecideContent formContent={getCurrentCriteria(pLineId ? (pLineId as ProductLineId) : null)} />;
});

export default HelpMeDecideWahooHealth;
